'use client'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { 
  GraduationCap,
  Users,
  Shield,
  Cloud,
  Database,
  CheckCircle2,
  Zap,
  BookOpen,
  Award,
  Laptop,
  Building2,
  Briefcase,
  Heart
} from 'lucide-react'

import { FadeUp } from '@/components/ui/fade-up'
const courses = {
  basic: {
    title: 'DX基礎コース',
    description: 'デジタル変革の基本概念から実践まで',
    icon: BookOpen,
    modules: [
      {
        title: 'DXとは何か',
        duration: '2時間',
        topics: ['DXの定義と必要性', '成功事例の分析', '自組織での応用']
      },
      {
        title: 'デジタルツール活用',
        duration: '3時間',
        topics: ['Microsoft 365活用', 'Google Workspace', 'コラボレーションツール']
      },
      {
        title: '業務効率化の基礎',
        duration: '2時間',
        topics: ['業務の可視化', '自動化の考え方', 'RPA入門']
      }
    ]
  },
  security: {
    title: '情報セキュリティコース',
    description: '組織と個人を守るセキュリティ知識',
    icon: Shield,
    modules: [
      {
        title: 'セキュリティ基礎',
        duration: '2時間',
        topics: ['脅威の種類と対策', 'パスワード管理', 'フィッシング対策']
      },
      {
        title: '組織のセキュリティ',
        duration: '3時間',
        topics: ['情報資産管理', 'インシデント対応', 'セキュリティポリシー']
      },
      {
        title: 'リモートワークセキュリティ',
        duration: '2時間',
        topics: ['VPNの活用', '端末管理', 'データ保護']
      }
    ]
  },
  cloud: {
    title: 'クラウド活用コース',
    description: 'クラウドサービスで業務を革新',
    icon: Cloud,
    modules: [
      {
        title: 'クラウド基礎',
        duration: '2時間',
        topics: ['クラウドとは', 'SaaS/PaaS/IaaS', 'メリットとリスク']
      },
      {
        title: '主要クラウドサービス',
        duration: '4時間',
        topics: ['AWS入門', 'Microsoft Azure', 'Google Cloud']
      },
      {
        title: 'クラウド移行実践',
        duration: '3時間',
        topics: ['移行計画', 'データ移行', 'コスト管理']
      }
    ]
  },
  data: {
    title: 'データ活用コース',
    description: 'データドリブンな意思決定を実現',
    icon: Database,
    modules: [
      {
        title: 'データ分析基礎',
        duration: '3時間',
        topics: ['データの種類', '基本的な分析手法', 'Excel活用']
      },
      {
        title: 'BIツール活用',
        duration: '4時間',
        topics: ['Power BI入門', 'Tableau基礎', 'ダッシュボード作成']
      },
      {
        title: 'データ活用実践',
        duration: '3時間',
        topics: ['KPI設定', 'レポート作成', 'データストーリーテリング']
      }
    ]
  }
}

const targetAudiences = [
  {
    icon: Building2,
    title: '自治体職員',
    description: '住民サービスのデジタル化、業務効率化を推進する職員向け',
    needs: ['基礎的なITリテラシー', '住民対応のデジタル化', 'セキュリティ意識']
  },
  {
    icon: Briefcase,
    title: '地域事業者',
    description: '中小企業の経営者・従業員のデジタル活用力向上',
    needs: ['生産性向上ツール', 'オンライン販売', 'データ分析']
  },
  {
    icon: Heart,
    title: 'NPO・団体職員',
    description: '限られたリソースで最大の成果を出すためのIT活用',
    needs: ['コスト効率的なツール', '情報発信力', 'オンライン募金']
  },
  {
    icon: GraduationCap,
    title: '学生・新社会人',
    description: '地域で活躍するための実践的ITスキル習得',
    needs: ['即戦力スキル', 'プロジェクト経験', '資格取得']
  }
]

const results = [
  {
    metric: '累計受講者',
    value: '8,500名+',
    description: '全国で開催'
  },
  {
    metric: '満足度',
    value: '94%',
    description: '「役立つ」以上の評価'
  },
  {
    metric: '実践率',
    value: '82%',
    description: '受講後1ヶ月以内に実践'
  },
  {
    metric: '生産性向上',
    value: '平均35%',
    description: '受講組織の業務効率'
  }
]

export function ItEducationContent() {
  return (
    <>
      {/* ヒーローセクション */}
      <section className="relative bg-gradient-to-br from-blue-600 via-cyan-600 to-teal-700 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 bg-[url('/images/grid.svg')] opacity-20"></div>
        <div
          className="animate-fade-up container relative z-10"
        >
          <div className="max-w-4xl mx-auto">
            <Badge className="mb-4 bg-white/20 text-white border-white/30">職員・事業者・NPO・学生向け</Badge>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              IT教育
            </h1>
            <p className="text-xl md:text-2xl mb-8 opacity-90">
              現場の生産性と再現可能な活用プロセス
            </p>
            <p className="text-lg mb-8 opacity-80">
              DX基礎、情報セキュリティ、クラウド活用、データ活用。<br />
              実践的なカリキュラムで、すぐに使えるスキルを身につけ、<br />
              組織全体のデジタル活用力を向上させます。
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" variant="secondary">
                研修プログラムを見る
              </Button>
              <Button size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                無料体験講座
              </Button>
            </div>
          </div>
        </div>
        <div className="absolute -bottom-1 left-0 right-0">
          <svg viewBox="0 0 1440 100" className="w-full h-24 fill-background">
            <path d="M0,50 C360,100 1080,0 1440,50 L1440,100 L0,100 Z" />
          </svg>
        </div>
      </section>

      {/* 実績 */}
      <section className="py-16">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">確かな実績</h2>
            <p className="text-lg text-muted-foreground">
              地域のIT人材育成をリードする豊富な経験
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-4 gap-6">
            {results.map((result, index) => (
              <FadeUp delay={index * 0.1}
                key={index}
              >
                <Card className="text-center">
                  <CardContent className="pt-6">
                    <div className="text-4xl font-bold text-primary mb-2">
                      {result.value}
                    </div>
                    <div className="font-semibold mb-1">{result.metric}</div>
                    <p className="text-sm text-muted-foreground">{result.description}</p>
                  </CardContent>
                </Card>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* コース一覧 */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">研修コース</h2>
            <p className="text-lg text-muted-foreground">
              レベルと目的に応じた体系的なカリキュラム
            </p>
          </FadeUp>

          <Tabs defaultValue="basic" className="max-w-5xl mx-auto">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="basic">DX基礎</TabsTrigger>
              <TabsTrigger value="security">セキュリティ</TabsTrigger>
              <TabsTrigger value="cloud">クラウド</TabsTrigger>
              <TabsTrigger value="data">データ活用</TabsTrigger>
            </TabsList>

            {Object.entries(courses).map(([key, course]) => {
              const Icon = course.icon
              return (
                <TabsContent key={key} value={key} className="mt-8">
                  <Card>
                    <CardHeader>
                      <div className="flex items-center gap-3">
                        <Icon className="h-8 w-8 text-primary" />
                        <div>
                          <CardTitle className="text-2xl">{course.title}</CardTitle>
                          <CardDescription>{course.description}</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-6">
                        {course.modules.map((module, idx) => (
                          <div key={idx} className="border-l-4 border-primary/20 pl-4">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-semibold">{module.title}</h4>
                              <Badge variant="secondary">{module.duration}</Badge>
                            </div>
                            <ul className="space-y-1">
                              {module.topics.map((topic, topicIdx) => (
                                <li key={topicIdx} className="flex items-center gap-2 text-sm text-muted-foreground">
                                  <CheckCircle2 className="h-4 w-4 text-primary" />
                                  <span>{topic}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-4 mt-6">
                        <Button>
                          詳細カリキュラムを見る
                        </Button>
                        <Button variant="outline">
                          お問い合わせ
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              )
            })}
          </Tabs>
        </div>
      </section>

      {/* 対象者別プログラム */}
      <section className="py-16">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">対象者別プログラム</h2>
            <p className="text-lg text-muted-foreground">
              それぞれのニーズに最適化されたカリキュラム
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-2 gap-8">
            {targetAudiences.map((audience, index) => {
              const Icon = audience.icon
              return (
                <FadeUp delay={index * 0.1}
                  key={index}
                >
                  <Card>
                    <CardHeader>
                      <div className="flex items-center gap-3">
                        <Icon className="h-10 w-10 text-primary" />
                        <div>
                          <CardTitle>{audience.title}</CardTitle>
                          <CardDescription>{audience.description}</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="font-semibold mb-3">重点学習項目</p>
                      <ul className="space-y-2">
                        {audience.needs.map((need, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm">
                            <Zap className="h-4 w-4 text-primary" />
                            <span>{need}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </FadeUp>
              )
            })}
          </div>
        </div>
      </section>

      {/* 研修形式 */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">柔軟な研修形式</h2>
            <p className="text-lg text-muted-foreground">
              組織の状況に合わせて最適な形式を選択
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                title: '集合研修',
                icon: Users,
                description: '対面でのインタラクティブな学習体験',
                features: [
                  '最大30名まで',
                  'ハンズオン実習',
                  'グループワーク',
                  '質疑応答'
                ],
                price: '1日 30万円〜'
              },
              {
                title: 'オンライン研修',
                icon: Laptop,
                description: '場所を選ばない効率的な学習',
                features: [
                  '最大100名まで',
                  'ライブ配信',
                  'ブレイクアウトルーム',
                  '録画提供'
                ],
                price: '1回 15万円〜'
              },
              {
                title: 'eラーニング',
                icon: BookOpen,
                description: '自分のペースで学べるオンデマンド学習',
                features: [
                  '無制限アクセス',
                  '進捗管理',
                  '理解度テスト',
                  '修了証発行'
                ],
                price: '月額 5千円/人〜'
              }
            ].map((format, index) => {
              const Icon = format.icon
              return (
                <FadeUp delay={index * 0.1}
                  key={index}
                >
                  <Card className="h-full">
                    <CardHeader>
                      <Icon className="h-12 w-12 text-primary mb-4" />
                      <CardTitle>{format.title}</CardTitle>
                      <CardDescription>{format.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2 mb-4">
                        {format.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm">
                            <CheckCircle2 className="h-4 w-4 text-primary" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="pt-4 border-t">
                        <p className="text-lg font-semibold">{format.price}</p>
                      </div>
                    </CardContent>
                  </Card>
                </FadeUp>
              )
            })}
          </div>
        </div>
      </section>

      {/* 導入事例 */}
      <section className="py-16">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">導入事例</h2>
            <p className="text-lg text-muted-foreground">
              IT教育で組織が変わった成功事例
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {[
              {
                organization: '福山市役所',
                program: '全職員向けDX基礎研修',
                participants: '職員1,200名',
                period: '6ヶ月間',
                results: [
                  'デジタルツール活用率90%達成',
                  '業務時間20%削減',
                  '市民満足度15%向上'
                ],
                quote: 'わかりやすい内容で、すぐに業務に活かせました'
              },
              {
                organization: '尾道商工会議所',
                program: '中小企業向けクラウド活用講座',
                participants: '経営者・従業員200名',
                period: '3ヶ月間',
                results: [
                  'クラウド導入企業80社',
                  'IT投資コスト平均30%削減',
                  'テレワーク実施率60%'
                ],
                quote: '具体的な導入方法がわかり、不安が解消されました'
              }
            ].map((caseStudy, index) => (
              <FadeUp delay={index * 0.1}
                key={index}
              >
                <Card>
                  <CardHeader>
                    <CardTitle>{caseStudy.organization}</CardTitle>
                    <CardDescription>{caseStudy.program}</CardDescription>
                    <div className="flex gap-2 mt-2">
                      <Badge variant="secondary">{caseStudy.participants}</Badge>
                      <Badge variant="secondary">{caseStudy.period}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="font-semibold mb-2">成果</p>
                      <ul className="space-y-1">
                        {caseStudy.results.map((result, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm">
                            <Award className="h-4 w-4 text-primary" />
                            <span>{result}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="pt-4 border-t">
                      <p className="text-sm text-muted-foreground italic">
                        &ldquo;{caseStudy.quote}&rdquo;
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-cyan-600 text-white">
        <div className="container">
          <FadeUp
            className="max-w-3xl mx-auto text-center"
          >
            <GraduationCap className="h-16 w-16 mx-auto mb-6 opacity-80" />
            <h2 className="text-3xl font-bold mb-4">
              IT教育で、組織の未来を変える
            </h2>
            <p className="text-xl mb-8 opacity-90">
              まずは無料体験講座で、実践的な学習を体験してください
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary">
                無料体験講座に申し込む
              </Button>
              <Button size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                研修プログラム資料
              </Button>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  )
}